import {
  Drawer,
  List,
  Divider,
  Toolbar,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";

import {
  ExpandLess,
  ExpandMore,
  Logout,
  People,
  Dashboard,
  Business,
  Gavel,
  IntegrationInstructions,
} from "@mui/icons-material";

import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

const drawerWidth = 260;

/* ================= Styles ================= */
const menuItemStyle = {
  borderRadius: 2,
  mx: 1,
  my: 0.5,
  color: "#E5E7EB",
  "& .MuiListItemIcon-root": {
    color: "#9CA3AF",
    minWidth: 36,
  },
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.08)",
  },
};

const activeStyle = {
  backgroundColor: "rgba(37,99,235,0.15)",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 8,
    bottom: 8,
    width: 4,
    borderRadius: 4,
    backgroundColor: "#2563EB",
  },
};

/* ================= Component ================= */
const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [role, setRole] = useState("");

  const [openDoctors, setOpenDoctors] = useState(false);
  const [openEmployees, setOpenEmployees] = useState(false);

  // ✅ فصلناهم (كان عندك openExternal يستخدم لقسمين)
  const [openCenterRelations, setOpenCenterRelations] = useState(false);
  const [openExternalRelations, setOpenExternalRelations] = useState(false);

  useEffect(() => {
    const storedRole = localStorage.getItem("userRole") || "";
    setRole(storedRole.toLowerCase().trim());
  }, []);

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          background: "linear-gradient(180deg, #024234ff, #020617)",
          color: "#E5E7EB",
          borderRight: "1px solid rgba(255,255,255,0.05)",
        },
      }}
    >
      <Toolbar />
      <Divider />

      <List>
        {/* ================= Dashboard ================= */}
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              ...menuItemStyle,
              ...(isActive("/dashboard") && activeStyle),
            }}
            onClick={() => navigate("/dashboard")}
          >
            <ListItemIcon>
              <Dashboard />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        {/* ================= Doctor Management ================= */}
        {(role === "admin" || role === "hr") && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                sx={menuItemStyle}
                onClick={() => setOpenDoctors(!openDoctors)}
              >
                <ListItemIcon>
                  <People />
                </ListItemIcon>
                <ListItemText primary="Doctor Management" />
                {openDoctors ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>

            <Collapse in={openDoctors} timeout="auto" unmountOnExit>
              <List sx={{ pl: 4 }}>
                {[
                  { label: "Doctor List", path: "/doctors" },
                  { label: "Add Doctor", path: "/doctors/add" },
                  { label: "Alerts", path: "/doctors/alerts" }, // ✅ حذفنا التكرار
                ].map((item) => (
                  <ListItemButton
                    key={item.path}
                    sx={{
                      ...menuItemStyle,
                      fontSize: 14,
                      ...(isActive(item.path) && activeStyle),
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* ================= Employee Management ================= */}
        {(role === "admin" || role === "hr") && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                sx={menuItemStyle}
                onClick={() => setOpenEmployees(!openEmployees)}
              >
                <ListItemIcon>
                  <People />
                </ListItemIcon>
                <ListItemText primary="Employee Management" />
                {openEmployees ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>

            <Collapse in={openEmployees} timeout="auto" unmountOnExit>
              <List sx={{ pl: 4 }}>
                {[
                  { label: "Employee List", path: "/employees" },
                  { label: "Add Employee", path: "/employees/add" },
                  { label: "Alerts", path: "/employees/alerts" },
                  { label: "Attendance", path: "/attendance" },
                  { label: "Leave Requests", path: "/leaves" },
                  { label: "Payroll", path: "/payroll" },
                  { label: "Reports", path: "/reports" },
                ].map((item) => (
                  <ListItemButton
                    key={item.path}
                    sx={{
                      ...menuItemStyle,
                      fontSize: 14,
                      ...(isActive(item.path) && activeStyle),
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* ================= Center Relations (Centers + Departments) ================= */}
        {(role === "admin" || role === "hr") && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                sx={menuItemStyle}
                onClick={() =>
                  setOpenCenterRelations(!openCenterRelations)
                }
              >
                <ListItemIcon>
                  <Business />
                </ListItemIcon>
                <ListItemText primary="Center Relations" />
                {openCenterRelations ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>

            <Collapse in={openCenterRelations} timeout="auto" unmountOnExit>
              <List sx={{ pl: 4 }}>
                {/* Centers */}
                <ListItemButton
                  sx={{
                    ...menuItemStyle,
                    ...(isActive("/centers") && activeStyle),
                  }}
                  onClick={() => navigate("/centers")}
                >
                  <ListItemIcon>
                    <IntegrationInstructions />
                  </ListItemIcon>
                  <ListItemText primary="Center List" />
                </ListItemButton>

                <ListItemButton
                  sx={menuItemStyle}
                  onClick={() => navigate("/centers/add")}
                >
                  <ListItemText primary="Add Center" />
                </ListItemButton>

                <ListItemButton
                  sx={menuItemStyle}
                  onClick={() => navigate("/centers/alerts")}
                >
                  <ListItemIcon>
                    <IntegrationInstructions />
                  </ListItemIcon>
                  <ListItemText primary="Center Alerts" />
                </ListItemButton>

                <Divider sx={{ my: 1, opacity: 0.15 }} />

                {/* Departments ✅ */}
                <ListItemButton
                  sx={{
                    ...menuItemStyle,
                    ...(isActive("/departments") && activeStyle),
                  }}
                  onClick={() => navigate("/departments")}
                >
                  <ListItemText primary="Department List" />
                </ListItemButton>

                <ListItemButton
                  sx={menuItemStyle}
                  onClick={() => navigate("/departments/add")}
                >
                  <ListItemText primary="Add Department" />
                </ListItemButton>

                <Divider sx={{ my: 1, opacity: 0.15 }} />

                {/* Contracts */}
                <ListItemButton
                  sx={menuItemStyle}
                  onClick={() => navigate("/contracts")}
                >
                  <ListItemIcon>
                    <Gavel />
                  </ListItemIcon>
                  <ListItemText primary="Contracts" />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        {/* ================= External Relations ================= */}
        {(role === "admin" || role === "hr") && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                sx={menuItemStyle}
                onClick={() =>
                  setOpenExternalRelations(!openExternalRelations)
                }
              >
                <ListItemIcon>
                  <Business />
                </ListItemIcon>
                <ListItemText primary="External Relations" />
                {openExternalRelations ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>

            <Collapse in={openExternalRelations} timeout="auto" unmountOnExit>
              <List sx={{ pl: 4 }}>
                <ListItemButton
                  sx={menuItemStyle}
                  onClick={() => navigate("/vendors")}
                >
                  <ListItemText primary="Vendors / Companies" />
                </ListItemButton>

                <ListItemButton
                  sx={menuItemStyle}
                  onClick={() => navigate("/integrations")}
                >
                  <ListItemIcon>
                    <IntegrationInstructions />
                  </ListItemIcon>
                  <ListItemText primary="Integrations" />
                </ListItemButton>

                <ListItemButton
                  sx={menuItemStyle}
                  onClick={() => navigate("/contracts")}
                >
                  <ListItemIcon>
                    <Gavel />
                  </ListItemIcon>
                  <ListItemText primary="Contracts" />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        {/* ================= Logout ================= */}
        <Divider sx={{ my: 2 }} />

        <ListItem disablePadding>
          <ListItemButton
            sx={{
              ...menuItemStyle,
              color: "#FCA5A5",
              "& .MuiListItemIcon-root": {
                color: "#F87171",
              },
              "&:hover": {
                backgroundColor: "rgba(239,68,68,0.15)",
              },
            }}
            onClick={() => {
              localStorage.clear();
              navigate("/login");
            }}
          >
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
};

export default NavBar;